import { useEffect, useRef, useState } from "react";
import { makeGetApiCall, makePostApiCall } from "../../shared/makeApiCall";

export interface EstimateOperation {
  id: string;
  status:
    "queued" | "preparing" | "submitting" | "reconciling" | "succeeded" | "failed" | "needs_review";
  message: string | null;
  can_retry: boolean;
}
interface EstimateStatusResponse {
  operation: EstimateOperation | null;
  estimate_id?: string | number | null;
  estimate_number?: string | null;
}
interface Props {
  orderId: string;
  connected: boolean;
  hasEstimate: boolean;
  onCreated: () => void;
}
const activeStatuses = ["queued", "preparing", "submitting", "reconciling"];

export default function OrderEstimateStatus({ orderId, connected, hasEstimate, onCreated }: Props) {
  const [status, setStatus] = useState<EstimateStatusResponse | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const control = useRef<{
    refresh?: () => Promise<void>;
    mutate?: (retry: boolean) => Promise<void>;
  }>({});
  const createdCallback = useRef(onCreated);
  createdCallback.current = onCreated;

  useEffect(() => {
    let alive = true;
    let reading = false;
    let mutating = false;
    let failures = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let notified = false;
    setStatus(null);
    setError("");
    setBusy(false);

    const accept = (next: EstimateStatusResponse) => {
      if (!alive) return;
      setStatus(next);
      setError("");
      const operation = next.operation;
      if ((operation?.status === "succeeded" || next.estimate_id) && !notified) {
        notified = true;
        // Hydrate IDs from automatic mobile/web creation, including historical success.
        createdCallback.current();
      }
      if (operation && activeStatuses.includes(operation.status)) timer = setTimeout(refresh, 2000);
    };
    const refresh = async () => {
      if (!alive || reading || mutating) return;
      if (timer) clearTimeout(timer);
      reading = true;
      try {
        const result = await makeGetApiCall<EstimateStatusResponse>(`order-estimates/${orderId}`);
        if (!alive) return;
        if (result.success) {
          failures = 0;
          accept(result.data);
        } else {
          setError("Could not read estimate status. Refresh status before taking another action.");
          if (++failures < 3) timer = setTimeout(refresh, 4000);
        }
      } finally {
        reading = false;
      }
    };
    const mutate = async (retry: boolean) => {
      if (!alive || mutating || reading) return;
      mutating = true;
      setBusy(true);
      if (timer) clearTimeout(timer);
      try {
        const result = await makePostApiCall<{ operation: EstimateOperation }>(
          `order-estimates/${orderId}${retry ? "/retry" : ""}`
        );
        if (!alive) return;
        if (result.success) accept({ operation: result.data.operation });
        else
          setError(
            "Queue acceptance could not be confirmed. Refresh status to check the existing request."
          );
      } finally {
        mutating = false;
        if (alive) {
          setBusy(false);
          void refresh();
        }
      }
    };
    control.current = { refresh, mutate };
    void refresh();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
      control.current = {};
    };
  }, [orderId]);

  const operation = status?.operation;
  const exists = hasEstimate || Boolean(status?.estimate_id) || operation?.status === "succeeded";
  const canCreate = connected && status !== null && !operation && !exists && !error;
  return (
    <section
      className="rounded-lg border border-gray-200 bg-white p-4"
      aria-label="QuickBooks estimate"
    >
      <p className="text-sm font-medium" role="status">
        {operation?.message ||
          (exists
            ? "QuickBooks estimate created."
            : status
              ? "No estimate request yet."
              : "Loading estimate status…")}
        {status?.estimate_number ? ` Estimate ${status.estimate_number}.` : ""}
      </p>
      {error && (
        <p className="mt-2 text-sm text-red-700" role="alert">
          {error}
        </p>
      )}
      <div className="mt-3 flex gap-3">
        {canCreate && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void control.current.mutate?.(false)}
            className="rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {busy ? "Queueing…" : "Queue QB Estimate"}
          </button>
        )}
        {operation?.can_retry && !error && !exists && connected && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void control.current.mutate?.(true)}
            className="rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {busy ? "Queueing…" : "Retry Estimate"}
          </button>
        )}
        <button
          type="button"
          disabled={busy}
          onClick={() => void control.current.refresh?.()}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm disabled:opacity-50"
        >
          Refresh status
        </button>
      </div>
    </section>
  );
}
